import { formatWeight, formatWeightChange, weightNumber, type LocalDate } from '@tahi/core';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { setWeightUnit } from '../../../data/commands/weight';
import { useCommandEnv } from '../../../data/hooks';
import { Button } from '../../../ui';
import { Segmented } from '../../../ui/Segmented';
import { GoalBar } from '../components/GoalBar';
import { LogSheet } from '../components/LogSheet';
import { TrendChart } from '../components/TrendChart';
import { copy } from '../copy';
import { dayLabel, rateText } from '../format';
import { useWeightOverview, WEIGHT_BASE } from '../hooks';
import { CARD, H1, HERO_NUM, LABEL, QUIET_LINK, SCREEN } from '../styles';

export function WeightDashboard({ startLogging = false }: { startLogging?: boolean }) {
  const o = useWeightOverview();
  const env = useCommandEnv();
  const navigate = useNavigate();
  const [sheetDay, setSheetDay] = useState<LocalDate | null>(null);
  const [opened, setOpened] = useState(startLogging);

  if (!o) {
    return (
      <main className={SCREEN}>
        <p role="status" className="text-muted">
          {copy.loading}
        </p>
      </main>
    );
  }
  const sheetOpen = opened || sheetDay !== null;
  const closeSheet = () => {
    setSheetDay(null);
    if (opened) {
      setOpened(false);
      // The /log shortcut route goes back to the dashboard once the sheet closes.
      if (startLogging) navigate(WEIGHT_BASE, { replace: true });
    }
  };
  const unit = o.unit;
  const hero = o.avg7 ?? o.latest?.valueG ?? null;
  const avgLabel = o.avg7 !== null ? formatWeight(o.avg7, unit) : null;

  return (
    <main className={SCREEN}>
      <header className="flex items-center justify-between gap-3">
        <h1 className={H1}>{copy.moduleName}</h1>
      </header>

      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start">
        <div className="flex flex-col gap-4">
          <section aria-label={copy.average7} className={CARD}>
            {hero === null ? (
              <p className="text-muted">{copy.empty}</p>
            ) : (
              <>
                <p className={LABEL}>{o.avg7 !== null ? copy.average7 : copy.latest}</p>
                <p className="mt-1 flex items-baseline gap-2">
                  <span className={HERO_NUM} data-testid="weight-hero">
                    {weightNumber(hero, unit)}
                  </span>
                  <span className="text-xl text-muted">{unit}</span>
                </p>
                {o.avgChange !== null ? (
                  <p className="mt-2 text-sm text-fg">
                    {copy.avgChange(formatWeightChange(o.avgChange, unit))}
                  </p>
                ) : null}
                {o.latest ? (
                  <p className="text-sm text-muted">
                    {copy.latest}: {formatWeight(o.latest.valueG, unit)} ·{' '}
                    {dayLabel(o.latest.date, o.today)}
                  </p>
                ) : null}
              </>
            )}
            <div className="mt-3">
              <Button
                variant="primary"
                onClick={() => (o.todayEntry ? setSheetDay(o.today) : setOpened(true))}
              >
                {o.todayEntry ? copy.editWeight : copy.logWeight}
              </Button>
            </div>
          </section>

          <section aria-label={copy.goal} className={CARD}>
            <div className="flex items-center justify-between gap-2">
              <h2 className={LABEL}>{copy.goal}</h2>
              <Link to={`${WEIGHT_BASE}/goal`} className={QUIET_LINK}>
                {o.settings.goalG !== null ? copy.editGoal : copy.setGoal}
              </Link>
            </div>
            <div className="mt-2">
              {o.goal ? (
                <GoalBar goal={o.goal} unit={unit} />
              ) : (
                <p className="text-sm text-muted">{copy.noGoal}</p>
              )}
            </div>
          </section>
        </div>

        <section aria-label={copy.trend} className={`${CARD} flex flex-col gap-3`}>
          <h2 className={LABEL}>{copy.trend}</h2>
          <TrendChart
            points={o.trend}
            unit={unit}
            goalG={o.settings.goalG}
            label={copy.trendLabel(o.trend.filter((p) => p.valueG !== null).length, avgLabel)}
          />
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <dt className="text-muted">{copy.average7}</dt>
              <dd className="font-mono tabular-nums text-fg" data-testid="weight-avg7">
                {avgLabel ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-muted">{copy.weeklyRate}</dt>
              <dd className="text-fg">{rateText(o.rate, unit)}</dd>
            </div>
          </dl>
        </section>

        {o.recent.length > 0 ? (
          <section aria-label={copy.recent} className={CARD}>
            <h2 className={LABEL}>{copy.recent}</h2>
            <ul className="mt-2 flex flex-col">
              {o.recent.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => setSheetDay(e.date)}
                    aria-label={`${dayLabel(e.date, o.today)}, ${formatWeight(e.valueG, unit)}`}
                    className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl px-2 text-left hover:bg-secondary focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[var(--ring)]"
                  >
                    <span className="min-w-0 truncate text-fg">
                      {dayLabel(e.date, o.today)}
                      {e.note ? <span className="text-muted"> · {e.note}</span> : null}
                    </span>
                    <span className="shrink-0 font-mono tabular-nums text-fg">
                      {formatWeight(e.valueG, unit)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section aria-label={copy.unit} className={`${CARD} flex flex-col gap-2`}>
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-fg">{copy.unit}</h2>
            <Segmented
              label={copy.unit}
              value={unit}
              options={[
                { value: 'kg', label: 'kg' },
                { value: 'lb', label: 'lb' },
              ]}
              onChange={(u) => void setWeightUnit(env, u)}
            />
          </div>
          <p className="text-sm text-muted">{copy.unitHint}</p>
        </section>
      </div>

      <LogSheet
        open={sheetOpen}
        onOpenChange={(v) => (v ? undefined : closeSheet())}
        date={sheetDay ?? o.today}
        today={o.today}
        unit={unit}
        entries={o.entries}
        lastValueG={o.latest?.valueG ?? null}
      />
    </main>
  );
}
